/**
 * React Native Crypto Adapter
 * @module @one-payments/adapters-native
 */

import CryptoJS from 'crypto-js';
import type { CryptoAdapter } from '@one-payments/core';

/**
 * Native Crypto Adapter using crypto-js with strict typing
 */
export class NativeCryptoAdapter implements CryptoAdapter {
  public async sha256(input: string | Uint8Array): Promise<string> {
    const data = typeof input === 'string' ? input : this.uint8ArrayToWordArray(input);
    const hash = CryptoJS.SHA256(data);
    return hash.toString(CryptoJS.enc.Hex);
  }

  public async hmacSHA256(secret: string, message: string): Promise<string> {
    const hmac = CryptoJS.HmacSHA256(message, secret);
    return hmac.toString(CryptoJS.enc.Hex);
  }

  public uuid(): string {
    // Simple UUID v4 implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  public randomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
  }

  private uint8ArrayToWordArray(uint8Array: Uint8Array): CryptoJS.lib.WordArray {
    const words: number[] = [];
    for (let i = 0; i < uint8Array.length; i++) {
      const wordIndex = i >>> 2;
      const byte = uint8Array[i];
      if (byte !== undefined) {
        words[wordIndex] = (words[wordIndex] ?? 0) | (byte << (24 - (i % 4) * 8));
      }
    }
    return CryptoJS.lib.WordArray.create(words, uint8Array.length);
  }
}
